import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import {
  ArrowLeft, Download, FileSpreadsheet, FileText, Search, Users,
  DollarSign, Clock, RefreshCw, Filter, Calendar, X, File, FileType, FileJson, BookOpen, Building,
  BarChart3, TrendingUp, AlertTriangle, Shield, Printer, Check, ChevronsUpDown
} from "lucide-react";
import { useAuth } from "../../App";
import { toast } from "sonner";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import { applyPlugin } from "jspdf-autotable";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { fmtBs, COLORS } from "./reportesUtils";

applyPlugin(jsPDF);

const AmbulantesMorosos = () => {
  const navigate = useNavigate();
  const { API, token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(false);
  const [ambMorosaData, setAmbMorosaData] = useState(null);
  const [ambMorosaMesesMin, setAmbMorosaMesesMin] = useState("");
  const [ambMorosaCategoria, setAmbMorosaCategoria] = useState("todas");
  const [ambMorosaFilters, setAmbMorosaFilters] = useState({ rif: "", nombre: "", categoria: "", ultimo: "", meses: "", monto: "" });

  useEffect(() => { fetchAmbulantesMorososData(); }, []);

  const fetchAmbulantesMorososData = async (aplicarFiltros = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (ambMorosaMesesMin) params.append("meses_mora_min", ambMorosaMesesMin);
      if (ambMorosaCategoria !== "todas") params.append("categoria", ambMorosaCategoria);
      const url = `${API}/reportes/ambulantes-morosos?${params.toString()}`;
      const response = await axios.get(url, { headers });
      setAmbMorosaData(response.data);
      if (aplicarFiltros) toast.success("Filtros aplicados correctamente");
    } catch (error) {
      toast.error("Error al cargar datos de morosidad ambulantes");
    } finally {
      setLoading(false);
    }
  };

  const limpiarFiltrosAmbMorosa = () => {
    setAmbMorosaMesesMin("");
    setAmbMorosaCategoria("todas");
    setAmbMorosaFilters({ rif: "", nombre: "", categoria: "", ultimo: "", meses: "", monto: "" });
    setTimeout(() => fetchAmbulantesMorososData(false), 0);
    toast.info("Filtros eliminados");
  };

  const hayFiltrosAmbMorosaActivos = ambMorosaMesesMin || ambMorosaCategoria !== "todas";
  const updateAmbMorosaFilter = (key, value) => setAmbMorosaFilters(prev => ({ ...prev, [key]: value }));
  const hayFiltrosAmbMorosaColActivos = Object.values(ambMorosaFilters).some(v => v !== "");

  const filteredAmbMorosaFilas = ambMorosaData?.filas?.filter(f => {
    const pf = ambMorosaFilters;
    if (pf.rif && !(f.rif || "").toLowerCase().includes(pf.rif.toLowerCase())) return false;
    if (pf.nombre && !(f.nombre_razon_social || "").toLowerCase().includes(pf.nombre.toLowerCase())) return false;
    if (pf.categoria && !(f.categoria || "").toLowerCase().includes(pf.categoria.toLowerCase())) return false;
    if (pf.ultimo && !(f.ultimo_pago || "").toLowerCase().includes(pf.ultimo.toLowerCase())) return false;
    if (pf.meses && !String(f.meses_morosos || 0).includes(pf.meses)) return false;
    if (pf.monto && !String(f.monto_adeudado || 0).includes(pf.monto)) return false;
    return true;
  }) || [];

  const ambMorosaTotalesFiltrados = filteredAmbMorosaFilas.reduce((acc, f) => ({
    meses_morosos: acc.meses_morosos + (f.meses_morosos || 0),
    monto_adeudado: acc.monto_adeudado + (f.monto_adeudado || 0),
  }), { meses_morosos: 0, monto_adeudado: 0 });

  const exportAmbMorosaToExcel = () => {
    if (!ambMorosaData) return;
    const wsData = [
      ["MOROSIDAD VENDEDORES AMBULANTES"], [],
      ["RIF", "CONTRIBUYENTE", "CATEGORÍA", "ÚLTIMO MES PAGADO", "MESES MOROSOS", "MONTO ADEUDADO"]
    ];
    filteredAmbMorosaFilas.forEach(f => {
      wsData.push([f.rif, f.nombre_razon_social, f.categoria, f.ultimo_pago, f.meses_morosos, f.monto_adeudado]);
    });
    wsData.push([]);
    wsData.push(["", "", "", "TOTALES", filteredAmbMorosaFilas.length + " registros", ambMorosaTotalesFiltrados.monto_adeudado]);
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, "AmbulantesMorosos");
    XLSX.writeFile(wb, `Ambulantes_Morosos_${new Date().toISOString().split("T")[0]}.xlsx`);
    toast.success("Exportado a Excel");
  };

  const exportAmbMorosaToPDF = () => {
    if (!ambMorosaData) return;
    const doc = new jsPDF("l", "mm", "a4");
    doc.setFontSize(16);
    doc.setTextColor(13, 148, 136);
    doc.text("MOROSIDAD VENDEDORES AMBULANTES", 14, 18);
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(`Total registros morosos: ${filteredAmbMorosaFilas.length}`, 14, 25);
    const headCols = ["RIF", "Contribuyente", "Categoría", "Último Pago", "Meses Morosos", "Monto Adeudado"];
    const body = filteredAmbMorosaFilas.map(f => [f.rif, f.nombre_razon_social, f.categoria, f.ultimo_pago, f.meses_morosos, fmtBs(f.monto_adeudado)]);
    body.push(["", "", "", "TOTALES", filteredAmbMorosaFilas.length + " registros", fmtBs(ambMorosaTotalesFiltrados.monto_adeudado)]);
    doc.autoTable({
      startY: 32, head: [headCols], body,
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [13, 148, 136], textColor: 255 },
      alternateRowStyles: { fillColor: [240, 253, 250] },
      didParseCell: (data) => { if (data.row.index === body.length - 1) { data.cell.styles.fontStyle = "bold"; data.cell.styles.fillColor = [204, 251, 241]; } }
    });
    doc.save(`Ambulantes_Morosos_${new Date().toISOString().split("T")[0]}.pdf`);
    toast.success("Exportado a PDF");
  };

  const exportAmbMorosaToCSV = () => {
    if (!ambMorosaData) return;
    const cols = ["RIF", "CONTRIBUYENTE", "CATEGORÍA", "ÚLTIMO PAGO", "MESES MOROSOS", "MONTO ADEUDADO"];
    const rows = filteredAmbMorosaFilas.map(f => [`"${f.rif}"`, `"${f.nombre_razon_social}"`, `"${f.categoria}"`, `"${f.ultimo_pago}"`, f.meses_morosos, f.monto_adeudado].join(","));
    const blob = new Blob(["\ufeff" + cols.join(",") + "\n" + rows.join("\n")], { type: "text/csv;charset=utf-8" });
    saveAs(blob, `Ambulantes_Morosos_${new Date().toISOString().split("T")[0]}.csv`);
    toast.success("Exportado a CSV");
  };

  const categoriasDisponibles = ambMorosaData?.resumen?.categorias || [];
  return (
    <div className="space-y-6" data-testid="reporte-ambulantes-morosos">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/reportes")} data-testid="ambmorosa-btn-volver">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
            <Users className="w-6 h-6 text-teal-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Morosidad Vendedores Ambulantes
            </h1>
            <p className="text-slate-500">Vendedores ambulantes con meses pendientes de pago</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => fetchAmbulantesMorososData(false)} disabled={loading} data-testid="ambmorosa-btn-actualizar">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={!ambMorosaData} data-testid="ambmorosa-btn-exportar">
                <Download className="w-4 h-4 mr-2" />
                Exportar
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={exportAmbMorosaToExcel} data-testid="ambmorosa-export-excel">
                <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportAmbMorosaToPDF} data-testid="ambmorosa-export-pdf">
                <FileText className="w-4 h-4 mr-2" /> PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportAmbMorosaToCSV} data-testid="ambmorosa-export-csv">
                <File className="w-4 h-4 mr-2" /> CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Filtros superiores */}
      <Card className="border-teal-200 bg-teal-50/30">
        <CardContent className="p-4">
          <div className="flex items-end gap-4 flex-wrap">
            <div className="w-[160px]">
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Meses Morosos Mín.</Label>
              <Input type="number" min="1" value={ambMorosaMesesMin} onChange={(e) => setAmbMorosaMesesMin(e.target.value)} className="bg-white" placeholder="Ej: 3" data-testid="ambmorosa-meses-min" />
            </div>
            <div className="w-[300px]">
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Categoría</Label>
              <Select value={ambMorosaCategoria} onValueChange={setAmbMorosaCategoria}>
                <SelectTrigger className="bg-white" data-testid="ambmorosa-categoria">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas</SelectItem>
                  {categoriasDisponibles.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => fetchAmbulantesMorososData(true)} disabled={loading} className="bg-teal-600 hover:bg-teal-700" data-testid="ambmorosa-btn-aplicar">
                <Search className="w-4 h-4 mr-2" /> Aplicar
              </Button>
              {hayFiltrosAmbMorosaActivos && (
                <Button variant="outline" onClick={limpiarFiltrosAmbMorosa} disabled={loading} data-testid="ambmorosa-btn-limpiar">
                  <X className="w-4 h-4 mr-2" /> Limpiar
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-teal-400" />
        </div>
      ) : ambMorosaData ? (
        <>
          {/* Tarjetas resumen */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">Vendedores Morosos</p>
                <p className="text-2xl font-bold text-teal-700" data-testid="ambmorosa-card-vendedores">{ambMorosaData.resumen?.total_vendedores_morosos || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">Registros Morosos</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="ambmorosa-card-registros">{ambMorosaData.resumen?.total_registros_morosos || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">Monto Total Adeudado</p>
                <p className="text-lg font-bold text-red-600" data-testid="ambmorosa-card-monto">{fmtBs(ambMorosaData.resumen?.monto_total_adeudado)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">Prom. Meses Morosos</p>
                <p className="text-2xl font-bold text-amber-600" data-testid="ambmorosa-card-promedio">{ambMorosaData.resumen?.promedio_meses_morosos || 0}</p>
              </CardContent>
            </Card>
          </div>

          {/* Gráficos */}
          {ambMorosaData.filas?.length > 0 && (() => {
            const filas = ambMorosaData.filas;

            // Distribución por Categoría
            const catCount = {};
            filas.forEach(f => { const c = f.categoria || "Sin categoría"; catCount[c] = (catCount[c] || 0) + 1; });
            const dataPieCat = Object.entries(catCount).sort((a,b) => b[1]-a[1]).map(([nombre, cantidad]) => ({ nombre: nombre.length > 25 ? nombre.substring(0,23) + "..." : nombre, cantidad }));

            // Distribución por Meses Morosos (agrupados)
            const mesesGroup = { "1-3 meses": 0, "4-6 meses": 0, "7-12 meses": 0, "13-24 meses": 0, "+24 meses": 0 };
            filas.forEach(f => {
              const m = f.meses_morosos || 0;
              if (m <= 3) mesesGroup["1-3 meses"]++;
              else if (m <= 6) mesesGroup["4-6 meses"]++;
              else if (m <= 12) mesesGroup["7-12 meses"]++;
              else if (m <= 24) mesesGroup["13-24 meses"]++;
              else mesesGroup["+24 meses"]++;
            });
            const dataBarMeses = Object.entries(mesesGroup).filter(([,v]) => v > 0).map(([nombre, cantidad]) => ({ nombre, cantidad }));

            // Severidad (color coding existente: amber < 3, orange 3-6, red > 6)
            const severidad = { "Leve (1-3m)": 0, "Media (4-6m)": 0, "Alta (+6m)": 0 };
            filas.forEach(f => {
              const m = f.meses_morosos || 0;
              if (m <= 3) severidad["Leve (1-3m)"]++;
              else if (m <= 6) severidad["Media (4-6m)"]++;
              else severidad["Alta (+6m)"]++;
            });
            const dataPieSev = Object.entries(severidad).filter(([,v]) => v > 0).map(([nombre, cantidad]) => ({ nombre, cantidad }));

            // Top 10 con mayor monto adeudado
            const topMontos = [...filas].filter(f => f.monto_adeudado > 0).sort((a,b) => b.monto_adeudado - a.monto_adeudado).slice(0, 10)
              .map(f => ({ nombre: (f.nombre_razon_social || "").substring(0, 25), monto: f.monto_adeudado }));

            const pieTeal = ["#0d9488", "#14b8a6", "#2dd4bf", "#5eead4", "#99f6e4", "#0f766e", "#115e59", "#134e4a"];
            const sevColors = { "Leve (1-3m)": "#fbbf24", "Media (4-6m)": "#f97316", "Alta (+6m)": "#ef4444" };

            return (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Pie: Categoría */}
                {dataPieCat.length > 0 && (
                  <Card>
                    <CardContent className="p-4">
                      <h3 className="text-sm font-semibold text-slate-700 mb-3">Distribución por Categoría</h3>
                      <ResponsiveContainer width="100%" height={260}>
                        <PieChart>
                          <Pie data={dataPieCat} cx="50%" cy="50%" outerRadius={90} dataKey="cantidad" nameKey="nombre"
                            label={({ nombre, percent }) => `${(nombre || "").substring(0,15)} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                            {dataPieCat.map((_, i) => (
                              <Cell key={i} fill={pieTeal[i % pieTeal.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}

                {/* Barras: Meses morosos agrupados */}
                <Card>
                  <CardContent className="p-4">
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">Distribución por Meses de Mora</h3>
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={dataBarMeses}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="nombre" tick={{ fontSize: 11 }} />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="cantidad" name="Vendedores" fill="#0d9488" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Pie: Severidad */}
                <Card>
                  <CardContent className="p-4">
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">Nivel de Severidad</h3>
                    <ResponsiveContainer width="100%" height={260}>
                      <PieChart>
                        <Pie data={dataPieSev} cx="50%" cy="50%" outerRadius={90} dataKey="cantidad" nameKey="nombre"
                          label={({ nombre, cantidad }) => `${nombre}: ${cantidad}`} labelLine={false}>
                          {dataPieSev.map((entry, i) => (
                            <Cell key={i} fill={sevColors[entry.nombre] || pieTeal[i]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Barras: Top contribuyentes con mayor deuda */}
                {topMontos.length > 0 && (
                  <Card>
                    <CardContent className="p-4">
                      <h3 className="text-sm font-semibold text-slate-700 mb-3">Top Vendedores con Mayor Deuda</h3>
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={topMontos} layout="vertical" margin={{ left: 10, right: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" tickFormatter={(v) => `Bs.${(v/1000).toFixed(0)}k`} />
                          <YAxis dataKey="nombre" type="category" width={130} tick={{ fontSize: 10 }} />
                          <Tooltip formatter={(v) => fmtBs(v)} />
                          <Bar dataKey="monto" name="Monto Adeudado" fill="#ef4444" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}
              </div>
            );
          })()}

          {/* Tabla */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Detalle de Morosidad</CardTitle>
                  <CardDescription>{filteredAmbMorosaFilas.length} de {ambMorosaData.filas?.length || 0} registros</CardDescription>
                </div>
                {hayFiltrosAmbMorosaColActivos && (
                  <Button variant="outline" size="sm" onClick={() => setAmbMorosaFilters({ rif: "", nombre: "", categoria: "", ultimo: "", meses: "", monto: "" })} data-testid="ambmorosa-limpiar-filtros-col">
                    <X className="w-4 h-4 mr-1" /> Limpiar filtros
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="ambmorosa-tabla">
                  <thead>
                    <tr className="border-b-2 border-teal-200 bg-teal-50">
                      <th className="text-left py-3 px-3 text-xs font-semibold text-teal-800 uppercase">RIF</th>
                      <th className="text-left py-3 px-3 text-xs font-semibold text-teal-800 uppercase">Contribuyente</th>
                      <th className="text-left py-3 px-3 text-xs font-semibold text-teal-800 uppercase">Categoría</th>
                      <th className="text-left py-3 px-3 text-xs font-semibold text-teal-800 uppercase">Último Mes Pagado</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-teal-800 uppercase">Meses Morosos</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-teal-800 uppercase">Monto Adeudado</th>
                    </tr>
                    <tr className="bg-teal-50/60 border-b border-teal-100">
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={ambMorosaFilters.rif} onChange={(e) => updateAmbMorosaFilter("rif", e.target.value)} data-testid="ambmorosa-filter-rif" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={ambMorosaFilters.nombre} onChange={(e) => updateAmbMorosaFilter("nombre", e.target.value)} data-testid="ambmorosa-filter-nombre" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={ambMorosaFilters.categoria} onChange={(e) => updateAmbMorosaFilter("categoria", e.target.value)} data-testid="ambmorosa-filter-categoria" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={ambMorosaFilters.ultimo} onChange={(e) => updateAmbMorosaFilter("ultimo", e.target.value)} data-testid="ambmorosa-filter-ultimo" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={ambMorosaFilters.meses} onChange={(e) => updateAmbMorosaFilter("meses", e.target.value)} data-testid="ambmorosa-filter-meses" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={ambMorosaFilters.monto} onChange={(e) => updateAmbMorosaFilter("monto", e.target.value)} data-testid="ambmorosa-filter-monto" /></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAmbMorosaFilas.length === 0 ? (
                      <tr><td colSpan={6} className="text-center py-12 text-slate-400">No se encontraron vendedores morosos</td></tr>
                    ) : (
                      filteredAmbMorosaFilas.map((f, idx) => (
                        <tr key={idx} className="border-b border-slate-100 hover:bg-teal-50/30 transition-colors">
                          <td className="py-2.5 px-3 text-sm font-mono text-slate-700">{f.rif}</td>
                          <td className="py-2.5 px-3 text-sm text-slate-800">{f.nombre_razon_social}</td>
                          <td className="py-2.5 px-3 text-sm">
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-teal-100 text-teal-800 truncate max-w-[200px] inline-block" title={f.categoria}>
                              {f.categoria.length > 30 ? f.categoria.substring(0, 30) + "..." : f.categoria}
                            </span>
                          </td>
                          <td className="py-2.5 px-3 text-sm text-slate-700">{f.ultimo_pago || "Sin registro"}</td>
                          <td className="py-2.5 px-3 text-sm text-right">
                            <span className={`font-bold ${f.meses_morosos >= 6 ? 'text-red-600' : f.meses_morosos >= 3 ? 'text-orange-600' : 'text-amber-600'}`}>
                              {f.meses_morosos}
                            </span>
                          </td>
                          <td className="py-2.5 px-3 text-sm text-right font-semibold text-red-700">{fmtBs(f.monto_adeudado)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {filteredAmbMorosaFilas.length > 0 && (
                    <tfoot>
                      <tr className="bg-teal-100 border-t-2 border-teal-300">
                        <td colSpan={4} className="py-3 px-3 text-sm font-bold text-teal-900 uppercase">Totales ({filteredAmbMorosaFilas.length} registros)</td>
                        <td className="py-3 px-3 text-sm text-right font-bold text-teal-900">Prom: {(ambMorosaTotalesFiltrados.meses_morosos / Math.max(filteredAmbMorosaFilas.length, 1)).toFixed(1)}</td>
                        <td className="py-3 px-3 text-sm text-right font-bold text-red-700">{fmtBs(ambMorosaTotalesFiltrados.monto_adeudado)}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No se pudieron cargar los datos del reporte</p>
            <Button variant="outline" onClick={() => fetchAmbulantesMorososData()} className="mt-4">
              <RefreshCw className="w-4 h-4 mr-2" /> Reintentar
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );

};

export default AmbulantesMorosos;
